// assistantStarters.js — the assistant's STARTER PROMPTS, per workspace.
//
// Nika 2026-09-10: "instead of 'ask anything about symbols' say 'ask anything
// about {workspace}', and make those options smart and more beautiful per
// workspace — not static or generic — generated on workspace use."
//
// Before this the drawer's empty state listed four English brochure lines
// ("What is Symbols?") plus every installed app's manifest starters in
// whatever language their author wrote (live on /w/gita/default: 16 chips,
// English and Georgian mixed, in a two-column grid that wrapped ragged).
//
// Two layers, ONE seam — `workspaceStarters(root)`, reached from the shared
// Chat.js Empty slot as `el.call('workspaceStarters', root)`:
//
//   1. BUILT — deterministic, instant, offline. One prompt per app the
//      workspace actually shows (the launcher's own visibility law:
//      searchAppGroupsFor → navItemVisible, so a flagged-off Mail never
//      offers a Mail prompt), the installed apps' own manifest starters
//      first, and one generic staple last. Every row carries the app's glyph
//      and name, so the list already reads as THIS workspace's.
//   2. GENERATED — on first use of the assistant in a workspace
//      (`ensureWorkspaceStarters`, fired by the Empty slot's onCreate and
//      re-armed by its render), a compact snapshot of the workspace — name,
//      language, the industry its Workspace Preset names (when one was
//      applied), the apps it shows, its team (a count and positions, never a
//      person's name, handle or email), the records already loaded, the apps'
//      own starters — goes to the AI through `sdk.ai.completion`
//      (the route the ticket modal's field synthesis already uses) under the
//      server purpose STARTER_PURPOSE (no tools, billed to no one), which
//      writes six prompts IN THE WORKSPACE LANGUAGE, grounded in that
//      snapshot. The set is cached in the member's preferences row
//      (`user_preferences.assistant_starters[workspaceId]`, beside
//      `navbar_tabs`) and regenerated when the app set, the language or a
//      week changes — so the list follows the workspace as it grows, and no
//      one pays a model call per open.
//
// The generated layer never blocks and never shifts layout: the built set
// paints first; the generated set replaces it when it lands (Rule 66 — the
// drawer's Sub line reports the wait, nothing spins). A failed generation
// leaves the built set in place and backs off for the session.
//
// Cache row shape (versioned; a shape change bumps STARTER_CACHE_VERSION and
// every older row simply reads as stale):
//   assistant_starters: { [workspaceId]: { v, lang, hash, at, items: [{ text, app }], rule? } }
//   v    — 2 since 2026-10-30. A v1 set could NAME another member: the
//          snapshot sent each member's name (the handle or the email when they
//          had no display name) and the prompt asked for members by name — on
//          dev a Harbor Homes member read "Check all pending tasks for <another
//          member's handle>". v2 sets come from a snapshot with no person in it
//          and pass the person filter before the write; the bump makes every
//          v1 set stale, so each member regenerates once.
//   rule — starterHash of the language's STARTER_LANGUAGE_RULES entry, when it has one.
//   hash — starterSignature(root): the visible app routes + installed app ids
//          + the manifest starter count. The team and the loaded records are in
//          the SNAPSHOT (they ground the prompts) but not in the hash — they
//          land asynchronously after boot and would make every first open
//          generate twice.

import _wsShared from '@symbo-ls/workspace-shared'
import { searchAppGroupsFor } from './searchApps.js'
import { installedAppRefs } from './workspaceConfigs.js'

// Deferred reads (barrel-init cycle — see pageContext.js's identical note):
// the shared barrel's default export is still undefined while this module
// evaluates, so nothing here destructures it at module scope.
const shellGetSDK = (...args) => _wsShared.functions.getSDK(...args)
const scopeNameOf = (root) => {
  const fn = _wsShared.functions && _wsShared.functions.assistantScopeName
  return typeof fn === 'function' ? fn(root) : ''
}

export const STARTER_MAX = 6
export const STARTER_TTL_MS = 7 * 24 * 60 * 60 * 1000
export const STARTER_FAIL_BACKOFF_MS = 10 * 60 * 1000
// The apps, tiles and manifest starters land on root in several waves after
// boot (navbar rows, then the takeover module, then the additive apps); each
// wave changes the signature. Measured on dev 2026-09-10: four generation
// calls in one second, one per wave. So a generation waits for the signature
// to hold still for this long before it spends a model call.
export const STARTER_SETTLE_MS = 1500
export const STARTER_CACHE_KEY = 'assistant_starters'
export const STARTER_CACHE_VERSION = 2
export const STARTER_CACHE_MAX_WORKSPACES = 8
export const STARTER_MODULE_MAX = 2
export const STARTER_TEXT_MAX = 90
export const STARTER_SNAPSHOT_MAX_CHARS = 6000
// The server-owned PURPOSE the generation names on its /turn call (server
// ephemeralPurposes.js). Nobody asked for these prompts, so the server runs
// the call with no tools, on the board model, billed to no one, inside a
// per-person daily quota (a 429 past it — the built set stays and the shell
// backs off like any failure). Measured before it (dev, 2026-10-25): 34–41
// credits per new member within a minute of signing in.
export const STARTER_PURPOSE = 'workspace.starters'

// Built-in app → the prompt pair it offers in the built set. Keys live in
// packages/shared/i18n/*/workspace.json; the tasks/calendar/generic pairs are
// the page-suggestion pairs pageContext.js already ships (Rule 63 — one key
// per idea), the rest are this file's own `workspace.aiStarter.*` pairs.
export const BUILTIN_STARTERS = [
  {
    route: '/tasks',
    labelKey: 'workspace.aiSuggest.tasks.blocked',
    promptKey: 'workspace.aiSuggest.tasks.blockedPrompt'
  },
  {
    route: '/calendar',
    labelKey: 'workspace.aiSuggest.calendar.agenda',
    promptKey: 'workspace.aiSuggest.calendar.agendaPrompt'
  },
  {
    route: '/mail',
    labelKey: 'workspace.aiStarter.mail.unread',
    promptKey: 'workspace.aiStarter.mail.unreadPrompt'
  },
  {
    route: '/chat',
    labelKey: 'workspace.aiStarter.chat.missed',
    promptKey: 'workspace.aiStarter.chat.missedPrompt'
  },
  {
    route: '/notes',
    labelKey: 'workspace.aiStarter.notes.recent',
    promptKey: 'workspace.aiStarter.notes.recentPrompt'
  },
  {
    route: '/files',
    labelKey: 'workspace.aiStarter.files.recent',
    promptKey: 'workspace.aiStarter.files.recentPrompt'
  },
  {
    route: '/meet',
    labelKey: 'workspace.aiStarter.meet.last',
    promptKey: 'workspace.aiStarter.meet.lastPrompt'
  },
  {
    route: '/infra',
    labelKey: 'workspace.aiStarter.infra.status',
    promptKey: 'workspace.aiStarter.infra.statusPrompt'
  },
  {
    route: '/team-members',
    labelKey: 'workspace.aiStarter.people.who',
    promptKey: 'workspace.aiStarter.people.whoPrompt'
  },
  {
    route: '/studio',
    labelKey: 'workspace.aiStarter.apps.installed',
    promptKey: 'workspace.aiStarter.apps.installedPrompt'
  }
]

const GENERIC_STARTER = {
  labelKey: 'workspace.aiSuggest.generic.myPlate',
  promptKey: 'workspace.aiSuggest.generic.myPlatePrompt'
}

// The customer's own app tiles (a takeover module's apps, an installed app's
// launcher tile) ship no prompt of their own — this pair, with the app's name
// in place of {app}, gives each one a grounded ask.
const CUSTOM_APP_STARTER = {
  labelKey: 'workspace.aiStarter.app.whatsNew',
  promptKey: 'workspace.aiStarter.app.whatsNewPrompt'
}

// Collections the shell loads onto root for its built-in apps; a module's
// own collections come from `workspaceProject.stateKeys`.
const ROOT_RECORD_KEYS = ['tickets', 'events', 'documents', 'notes', 'channels', 'fileNodes']

const LANGUAGE_NAMES = { en: 'English', ka: 'Georgian' }

// Per-language register rules for the generator. Georgian speaks in the
// shell's polite plural (თქვენ) — the register every keyed starter uses — so a
// generated request reads like the rows around it. A cached set records the
// rule it was made under (`rule`, its hash): a changed rule re-generates that
// language's sets once, and a language with no rule is untouched.
export const STARTER_LANGUAGE_RULES = {
  ka: '- Write every Georgian "text" in the polite plural (თქვენ) register: a request uses the -თ imperative (შეაჯამეთ, ჩამომითვალეთ, მაჩვენეთ), never the informal singular (შეაჯამე, მაჩვენე).'
}

const str = (v) => (v == null ? '' : String(v))
const titleOf = (rec) =>
  rec && typeof rec === 'object'
    ? str(rec.title || rec.name || rec.label || rec.subject || rec.displayName)
    : str(rec)

// The INDUSTRY this workspace was set up for: the key of the Workspace Preset
// the server applied (`settings.preset.key`, written by the preset apply — see
// marketplace/functions/workspacePresets.js workspacePresetApplied). The key is
// DATA, never copy: the generator reads it and writes the prompts in the
// workspace language, so no industry's words live in code. `custom` is "Something
// else" (a blueprint with no industry), not an industry. '' when no preset is
// recorded (a workspace made by hand, or a record that carries no settings).
const industryOf = (root) => {
  const settings = root && root.workspace && root.workspace.settings
  const preset = settings && typeof settings === 'object' ? settings.preset : null
  const key = preset && typeof preset === 'object' ? str(preset.key).trim() : ''
  return key && key !== 'custom' ? key : ''
}

// djb2 → base36. A change signature, not a security hash.
export const starterHash = (input) => {
  const t = str(input)
  let h = 5381
  for (let i = 0; i < t.length; i++) h = ((h << 5) + h + t.charCodeAt(i)) | 0
  return (h >>> 0).toString(36)
}

// The hash of a language's register rule, '' when it has none.
const starterRuleOf = (lang) => {
  const rule = STARTER_LANGUAGE_RULES[str(lang)]
  return rule ? starterHash(rule) : ''
}

export const starterWorkspaceId = (root) => {
  if (!root || typeof root !== 'object') return null
  const ws = root.workspace
  const id = (ws && (ws.id || ws._id)) || (root.jwtClaims && root.jwtClaims.active_workspace_id)
  return id ? String(id) : null
}

// The apps this workspace SHOWS — the launcher's own groups (custom tiles
// first, then the house apps), already through the feature-flag / app-access
// gate. Admin tiles are chrome, not work, so they never seed a prompt.
//   → [{ route, title, icon, custom, appKey?, projectId? }]  (title: a bundle
//     key for a house app, the customer's own words for a custom tile —
//     `polyglot` resolves both; `appKey`: an installed app's record key,
//     stamped on its one launcher tile by workspaceConfigs.js — see
//     namedStarterTiles; `projectId`: a built app's project, from its tile —
//     workspaceConfigs.js extensionAppRows)
export const visibleStarterTiles = (root) => {
  let groups = []
  try {
    groups = searchAppGroupsFor(root) || []
  } catch (_) {
    groups = []
  }
  const out = []
  const seen = new Set()
  for (const g of groups) {
    if (!g || g.key === 'admin') continue
    for (const a of g.apps || []) {
      const route = str(a && a.route)
      if (!route || seen.has(route)) continue
      seen.add(route)
      out.push({
        route,
        title: str(a.title),
        icon: str(a.icon) || 'blocks',
        custom: g.key === 'workspace-apps',
        ...(a.appKey ? { appKey: str(a.appKey) } : {}),
        ...(a.projectId ? { projectId: str(a.projectId) } : {})
      })
    }
  }
  return out
}

// The tiles as the member READS them: an installed app's tile (it carries
// `appKey`) by its App Store name, through installedAppName — Nika
// 2026-10-23: "app names = what a person sees in the workspace App Store".
// marcus's 390 px walk (2026-10-24) read a home suggestion captioned
// "Analytics" — bi-app's manifest tile — where the App Store says Insights.
// The rows below, the generator's snapshot and the model's `app` match all
// read this one title. A house tile's key and a customer's own words stay as
// they are (the builders resolve them through polyglot); with no element (a
// direct import call) every tile stays as it is.
const namedStarterTiles = (el, tiles) =>
  tiles.map((t) => {
    if (!t.appKey || !el || typeof el.call !== 'function') return t
    const name = el.call('installedAppName', { appKey: t.appKey, title: t.title })
    return typeof name === 'string' && name ? { ...t, title: name } : t
  })

// The installed apps' own starters (`root.workspaceProject.chatStarters`,
// composed by workspaceProjects.js from every manifest) → [{ text, localAction? }]
export const moduleStarters = (root) => {
  const list = root && root.workspaceProject && root.workspaceProject.chatStarters
  if (!Array.isArray(list)) return []
  const out = []
  for (const it of list) {
    if (typeof it === 'string') {
      const t = it.trim()
      if (t) out.push({ text: t })
    } else if (it && typeof it === 'object' && typeof it.text === 'string' && it.text.trim()) {
      // A starter that names the app's OWN FORM (`form: '<form id>'`) opens
      // it locally (assistantAppForms.js assistantStarterForm) — no page, no
      // assistant turn (boards-r1 follow-up b).
      const formId = typeof it.form === 'string' && /^[a-z0-9-]+$/.test(it.form) ? it.form : ''
      const appId = str(it.app || root.workspaceProject.id).trim()
      if (formId && appId) {
        out.push({ text: it.text.trim(), localAction: 'assistantStarterForm', form: appId + '.' + formId })
        continue
      }
      out.push({
        text: it.text.trim(),
        localAction: typeof it.localAction === 'string' ? it.localAction : undefined
      })
    }
  }
  return out
}

export const starterSignature = (root, tiles) => {
  const t = tiles || visibleStarterTiles(root)
  const routes = t.map((x) => x.route).sort()
  const apps = (Array.isArray(root && root.workspaceApps) ? root.workspaceApps : [])
    .map((a) => str(a && (a.id || a.appId)))
    .filter(Boolean)
    .sort()
  return starterHash(JSON.stringify({ routes, apps, m: moduleStarters(root).length }))
}

export const readStarterCache = (root, wsId) => {
  const prefs = root && root.userPrefs
  const all = prefs && typeof prefs === 'object' ? prefs[STARTER_CACHE_KEY] : null
  const entry = all && typeof all === 'object' && wsId ? all[wsId] : null
  return entry && typeof entry === 'object' ? entry : null
}

export const starterCacheFresh = (entry, { lang, hash, now }) =>
  !!entry &&
  entry.v === STARTER_CACHE_VERSION &&
  entry.lang === lang &&
  entry.hash === hash &&
  str(entry.rule) === starterRuleOf(lang) &&
  Array.isArray(entry.items) &&
  entry.items.length > 0 &&
  typeof entry.at === 'number' &&
  now - entry.at >= 0 &&
  now - entry.at < STARTER_TTL_MS

// Keep the newest STARTER_CACHE_MAX_WORKSPACES rows — the preferences row is
// one document per member, and a member who visits many workspaces must not
// grow it without bound.
export const trimStarterCache = (all) => {
  const src = all && typeof all === 'object' ? all : {}
  const ids = Object.keys(src)
    .filter((id) => src[id] && typeof src[id] === 'object')
    .sort((a, b) => (src[b].at || 0) - (src[a].at || 0))
    .slice(0, STARTER_CACHE_MAX_WORKSPACES)
  const kept = {}
  for (const id of ids) kept[id] = src[id]
  return kept
}

// ── No person in a starter (2026-10-30) ─────────────────────────────────────
// A generated tile named another member by handle (dev, Harbor Homes: "Check
// all pending tasks for <handle>"; a public walkthrough video showed it). The
// snapshot sent each member's name (the handle or the email when they had no
// display name) and the prompt asked the model to name members. Now the
// snapshot holds no person and no email address, the prompt forbids naming a
// person, and a generated item that names one never reaches the cache.

// True when the text holds an email address.
const starterHasEmail = (text) => /[^\s@]+@[^\s@]+\.[^\s@]+/.test(str(text))

// One email gate over everything the snapshot carries: a list drops a string
// that holds an address, a single field reads ''.
const starterWithoutEmails = (v) => {
  if (typeof v === 'string') return starterHasEmail(v) ? '' : v
  if (Array.isArray(v)) {
    return v.filter((x) => typeof x !== 'string' || !starterHasEmail(x)).map(starterWithoutEmails)
  }
  if (v && typeof v === 'object') {
    const out = {}
    for (const k of Object.keys(v)) out[k] = starterWithoutEmails(v[k])
    return out
  }
  return v
}

// The team as the generator sees it: the positions its members hold
// (`position`, else `title`, else `role`) — unique, non-empty, at most five.
// Never a name, a handle or an email address.
const starterPositions = (people) => {
  const out = []
  const seen = new Set()
  for (const p of people) {
    const pos = str(p && (p.position || p.title || p.role)).trim()
    const k = pos.toLowerCase()
    if (!pos || starterHasEmail(pos) || seen.has(k)) continue
    seen.add(k)
    out.push(pos)
    if (out.length >= 5) break
  }
  return out
}

// The words that identify a member of THIS workspace: name, full name,
// username or handle, email and the email's local part — lower-case, 3+
// characters (a shorter one matches ordinary words). Read locally from
// root.peopleAll for the person filter; they never go to the model.
const starterMemberIds = (people) => {
  const ids = new Set()
  for (const p of Array.isArray(people) ? people : []) {
    if (!p || typeof p !== 'object') continue
    for (const v of [p.name, p.fullName, p.username, p.handle, p.email]) {
      if (typeof v !== 'string') continue
      const id = v.trim().replace(/^@+/, '').toLowerCase()
      for (const w of id.includes('@') ? [id, id.split('@')[0]] : [id]) {
        if (w.length >= 3) ids.add(w)
      }
    }
  }
  return [...ids]
}

// True when the text names one of the ids as a whole word, in any case: a
// letter, a digit or an underscore next to the match makes it part of a
// longer word, not the id.
const starterNamesMember = (text, ids) => {
  const hay = str(text).toLowerCase()
  const wordChar = (c) => !!c && /[\p{L}\p{N}_]/u.test(c)
  for (const id of ids) {
    for (let at = hay.indexOf(id); at !== -1; at = hay.indexOf(id, at + 1)) {
      if (!wordChar(hay[at - 1]) && !wordChar(hay[at + id.length])) return true
    }
  }
  return false
}

// The person filter, before the cache write (defense in depth — the model
// never saw a name, but a record title can carry one): an item that holds an
// email address or names a member of this workspace is dropped. The rest is
// kept as it is, never padded.
const starterItemsWithoutPeople = (items, people) => {
  const ids = starterMemberIds(people)
  return items.filter((it) => !starterHasEmail(it.text) && !starterNamesMember(it.text, ids))
}

// The compact, model-readable picture of the workspace the generator grounds
// its prompts in. Bounded twice: per list, and in total (STARTER_SNAPSHOT_
// MAX_CHARS) — samples go first, then the apps' starters, then the app list
// itself is cut, so a big workspace never blows the dispatch. It names no
// person: the team is a count and positions, and no string with an email
// address in it goes out.
export const starterSnapshot = (root, { resolve, tiles, now } = {}) => {
  const r = root && typeof root === 'object' ? root : {}
  const res = typeof resolve === 'function' ? resolve : (k) => k
  const t = tiles || visibleStarterTiles(r)
  const ws = r.workspace || {}
  const org = r.org || {}
  const people = Array.isArray(r.peopleAll) ? r.peopleAll : []
  const stateKeys =
    r.workspaceProject && Array.isArray(r.workspaceProject.stateKeys)
      ? r.workspaceProject.stateKeys
      : []
  const records = []
  const seenKeys = new Set()
  for (const key of [...ROOT_RECORD_KEYS, ...stateKeys]) {
    if (!key || seenKeys.has(key)) continue
    seenKeys.add(key)
    const list = r[key]
    if (!Array.isArray(list) || !list.length) continue
    records.push({
      key,
      count: list.length,
      sample: list.slice(0, 3).map(titleOf).filter(Boolean)
    })
    if (records.length >= 12) break
  }
  const snap = starterWithoutEmails({
    workspace: scopeNameOf(r) || str(ws.displayName || ws.name),
    slug: str(ws.slug),
    organization: str(org.name),
    ...(industryOf(r) ? { industry: industryOf(r) } : {}),
    language: str(r.lang) || 'en',
    date: new Date(typeof now === 'number' ? now : Date.now()).toISOString().slice(0, 10),
    members: { count: people.length, positions: starterPositions(people) },
    apps: t.slice(0, 24).map((x) => ({ route: x.route, title: str(res(x.title)) })),
    // Through the bundle, like the built set's rows: a manifest starter may
    // be a KEY the app ships (Storm: { text: 'valStarterAttention', route })
    // — sent raw, the generator read 'valStarterAttention' as a prompt.
    appStarters: moduleStarters(r)
      .slice(0, 12)
      .map((s) => str(res(s.text)) || s.text),
    records
  })
  const size = () => JSON.stringify(snap).length
  if (size() > STARTER_SNAPSHOT_MAX_CHARS) for (const rec of snap.records) delete rec.sample
  if (size() > STARTER_SNAPSHOT_MAX_CHARS) snap.appStarters = []
  if (size() > STARTER_SNAPSHOT_MAX_CHARS) snap.members.positions = []
  if (size() > STARTER_SNAPSHOT_MAX_CHARS) snap.apps = snap.apps.slice(0, 12)
  return snap
}

export const starterSystemPrompt = (lang) => {
  const code = str(lang) || 'en'
  const name = LANGUAGE_NAMES[code] || code
  return [
    'You write starter prompts for the AI assistant of a team workspace.',
    `Reply with ONLY a JSON array of exactly ${STARTER_MAX} objects, each {"text": string, "app": string|null}. No prose, no markdown, no code fence.`,
    'Rules:',
    `- Write every "text" in ${name} (language code "${code}").`,
    ...(STARTER_LANGUAGE_RULES[code] ? [STARTER_LANGUAGE_RULES[code]] : []),
    '- Each "text" is ONE short question or request (at most 60 characters) that a member of this workspace would ask the assistant about the workspace\'s own apps, records, people or figures.',
    '- Ground every item in the snapshot: name the apps, record types or figures it lists. Never invent apps or data that are not in the snapshot.',
    '- Never name a person (no member names, handles or email addresses); speak of "the team", a role, or "my".',
    '- When the snapshot has an "industry", the workspace runs a business of that kind: ask what that business does day to day, and favour the apps that serve it over the others.',
    '- Spread the items across different apps; at most two items about the same app.',
    '- "app" is the "route" of the snapshot app the item concerns, copied exactly, or null.',
    '- No numbering, no emoji, no quotation marks inside "text".'
  ].join('\n')
}

export const starterUserMessage = (snapshot) =>
  'Workspace snapshot (JSON):\n' + JSON.stringify(snapshot)

// The completion result ladder the ticket modal's synthesis already walks —
// sdk.ai.completion answers in one of these shapes depending on the mode.
export const completionText = (result) => {
  if (typeof result === 'string') return result
  if (!result || typeof result !== 'object') return ''
  if (typeof result.text === 'string') return result.text
  if (typeof result.content === 'string') return result.content
  if (result.message) {
    return typeof result.message === 'string' ? result.message : str(result.message.content)
  }
  if (result.data) {
    if (typeof result.data === 'string') return result.data
    if (typeof result.data.text === 'string') return result.data.text
    return JSON.stringify(result.data)
  }
  return ''
}

// The model's reply → [{ text, app }]. Tolerates a code fence, prose around
// the array, a bare array of strings; drops blanks, near-empties, oversize
// lines and duplicates; caps at STARTER_MAX. Anything unparseable → [] (the
// caller keeps the built set — never a half-list).
export const parseStarterReply = (text) => {
  const raw = str(text)
    .replace(/```[a-z]*\n?/gi, '')
    .trim()
  const start = raw.indexOf('[')
  if (start < 0) return []
  let depth = 0
  let end = -1
  let inStr = false
  let esc = false
  for (let i = start; i < raw.length; i++) {
    const c = raw[i]
    if (inStr) {
      if (esc) esc = false
      else if (c === '\\') esc = true
      else if (c === '"') inStr = false
      continue
    }
    if (c === '"') inStr = true
    else if (c === '[') depth++
    else if (c === ']') {
      depth--
      if (depth === 0) {
        end = i
        break
      }
    }
  }
  if (end < 0) return []
  let arr
  try {
    arr = JSON.parse(raw.slice(start, end + 1))
  } catch (_) {
    return []
  }
  if (!Array.isArray(arr)) return []
  const out = []
  const seen = new Set()
  for (const it of arr) {
    const textOf = typeof it === 'string' ? it : it && typeof it === 'object' ? str(it.text) : ''
    const t = textOf
      .replace(/\s+/g, ' ')
      .trim()
      .replace(/^["'“”„«»]+|["'“”„«»]+$/g, '')
      .trim()
    if (t.length < 3 || t.length > STARTER_TEXT_MAX) continue
    const k = t.toLowerCase()
    if (seen.has(k)) continue
    seen.add(k)
    const app =
      it && typeof it === 'object' && typeof it.app === 'string' && it.app.trim()
        ? it.app.trim()
        : null
    out.push({ text: t, app })
    if (out.length >= STARTER_MAX) break
  }
  return out
}

// The caption names the app a row concerns — but only when the label does
// not already say it (design consult 2026-09-10: a caption that repeats the
// label's own words costs a line per row and makes the list ragged).
export const starterCaption = (label, appTitle) => {
  const t = str(appTitle).trim()
  if (!t) return ''
  return str(label).toLowerCase().indexOf(t.toLowerCase()) === -1 ? t : ''
}

// Generated items → rows for AssistantStarter. An item's `app` (a route, or
// the app's name as the model saw it) pins the row to that app's glyph and
// name; an item about the workspace as a whole gets the assistant's glyph.
// The row keeps the app it is pinned to — its route, its name, a built app's
// project id — so a Fly home tile asks about THAT app (assistantFlyStart.js
// assistantFlyStartAsk). Dev walks round 5 (2026-10-29): tomas's "Check dock
// scheduling board for today" was pinned to his built app and its page read
// the preset Scheduling app — the row had kept its words only.
export const generatedStarterRows = (items, tiles, resolve) => {
  const res = typeof resolve === 'function' ? resolve : (k) => k
  const t = Array.isArray(tiles) ? tiles : []
  const byRoute = new Map(t.map((x) => [x.route, x]))
  const byTitle = new Map(t.map((x) => [str(res(x.title)).trim().toLowerCase(), x]))
  return (Array.isArray(items) ? items : []).map((it, i) => {
    const app = str(it && it.app).trim()
    const tile = (app && (byRoute.get(app) || byTitle.get(app.toLowerCase()))) || null
    const text = str(it && it.text)
    return {
      key: 'gen-' + i + '-' + starterHash(text),
      text,
      prompt: text,
      icon: tile ? tile.icon : 'magicstar',
      caption: tile ? starterCaption(text, res(tile.title)) : '',
      route: tile ? str(tile.route) : '',
      appName: tile ? str(res(tile.title)) : '',
      appId: tile && tile.projectId ? str(tile.projectId) : ''
    }
  })
}

// The BUILT set: the apps' own starters first (this workspace's domain), then
// one prompt per app the workspace shows in launcher order (custom tiles
// before house apps — that is the launcher's order too), then the generic
// staple. Capped at STARTER_MAX, de-duplicated by text. The LAST slot is
// reserved for the staple: the generic ask is always offered, last, so the
// workspace's own rows never crowd out the one prompt every member can use
// (2026-08-26 per Nika "user might want to go with generic choice").
export const builtStarterRows = (root, tiles, resolve) => {
  const r = root && typeof root === 'object' ? root : {}
  const res = typeof resolve === 'function' ? resolve : (k) => k
  const t = Array.isArray(tiles) ? tiles : visibleStarterTiles(r)
  const scopeName = scopeNameOf(r)
  const rows = []
  const seen = new Set()
  const push = (row, cap = STARTER_MAX - 1) => {
    const k = str(row.text).trim().toLowerCase()
    if (!k || seen.has(k) || rows.length >= cap) return
    seen.add(k)
    rows.push(row)
  }
  // The apps' own starters carry no caption: the Hint above already names
  // the workspace, and a manifest starter names its own subject.
  //
  // THROUGH THE BUNDLE (2026-09-26, QA on dev/natali: "Add a contact" and
  // "Schedule a booking" rendered in English in a Georgian workspace). A
  // manifest starter is resolved like every other string here: a bundle KEY
  // the module ships (its translations merge into the shell's polyglot map
  // on load) reads in the session language, and a plain literal passes
  // through verbatim — `translate()` returns what it cannot resolve, the
  // property that makes key-in-data safe (the navbar labels rely on it).
  // The row's key stays hashed on the manifest's own text, so it survives a
  // language switch.
  //
  // A manifest starter that is NO bundle key reads only in its author's own
  // words, and a manifest names no language: the words' script is the
  // evidence. In a session whose language writes in its own script — Georgian;
  // every system app's manifest is English (apps/*/symbols/config.js) — a
  // literal shows only when it is written in that script (dev 2026-10-23, a
  // Georgian session before its generated set: "Schedule a booking" was the
  // one English line on screen). The others wait for the generated set, which
  // writes every prompt in the session language; a keyed starter still shows,
  // translated.
  const SCRIPT_OF = { ka: /[Ⴀ-ჿᲐ-Ჿ]/ }
  const script = SCRIPT_OF[str(r.lang) || 'en']
  moduleStarters(r)
    .filter((st) => !script || str(res(st.text)) !== str(st.text) || script.test(st.text))
    .slice(0, STARTER_MODULE_MAX)
    .forEach((st, i) => {
      const text = str(res(st.text)) || st.text
      push({
        key: 'mod-' + i + '-' + starterHash(st.text),
        text,
        prompt: text,
        localAction: st.localAction,
        ...(st.form ? { form: st.form } : {}),
        icon: 'magicstar',
        caption: ''
      })
    })
  for (const tile of t) {
    const title = str(res(tile.title))
    const builtin = BUILTIN_STARTERS.find((b) => b.route === tile.route)
    if (builtin) {
      const text = str(res(builtin.labelKey)).replace('{workspace}', scopeName)
      push({
        key: 'app-' + tile.route,
        text,
        prompt: str(res(builtin.promptKey)).replace('{workspace}', scopeName),
        icon: tile.icon,
        caption: starterCaption(text, title)
      })
    } else if (tile.custom && title) {
      // "{app}: what is new?" already names the app → no caption.
      push({
        key: 'app-' + tile.route,
        text: str(res(CUSTOM_APP_STARTER.labelKey)).replace('{app}', title),
        prompt: str(res(CUSTOM_APP_STARTER.promptKey)).replace('{app}', title),
        icon: tile.icon,
        caption: ''
      })
    }
  }
  push(
    {
      key: 'generic-plate',
      text: str(res(GENERIC_STARTER.labelKey)),
      prompt: str(res(GENERIC_STARTER.promptKey)),
      icon: 'magicstar',
      caption: ''
    },
    STARTER_MAX
  )
  return rows
}

// THE seam the shared Chat.js Empty slot calls: `el.call('workspaceStarters',
// root)` → rows for AssistantStarter. Fresh generated set → those rows; else
// the built set. `this` is the calling element (polyglot resolution); a
// direct import call (tests) resolves keys to themselves.
export const workspaceStarters = function (root) {
  const el = this
  const r = root || (el && el.state && el.state.root)
  if (!r || typeof r !== 'object') return []
  const resolve = (key) => {
    try {
      return el && typeof el.call === 'function' ? el.call('polyglot', key) : key
    } catch (_) {
      return key
    }
  }
  const tiles = namedStarterTiles(el, visibleStarterTiles(r))
  const wsId = starterWorkspaceId(r)
  const lang = str(r.lang) || 'en'
  const cached = wsId ? readStarterCache(r, wsId) : null
  if (starterCacheFresh(cached, { lang, hash: starterSignature(r, tiles), now: Date.now() })) {
    return generatedStarterRows(cached.items, tiles, resolve)
  }
  return builtStarterRows(r, tiles, resolve)
}

// Persist a generated set under the home-prefs durability law (functions/
// homeLook.js; the dock writer, functions/assistantDock.js): the preferences
// `update` REPLACES the row, so the write is chained on the SAME per-
// workspace queue every writer of the row uses (`root.__navbarSaveQueue`),
// its payload — the cache merge included — is composed at SEND time from the
// LIVE row, and a row that has not loaded is READ first. The optimistic root
// write (the drawer re-renders at once) runs ONLY when the row has loaded: a
// partial `{ assistant_starters }` copy of an unloaded row would read as the
// whole row to the next writer and erase every other stored preference (the
// data-loss defect of a cold boot, or of the window after a workspace switch
// wipes `userPrefs`). Unloaded, the confirmed row lands in the state instead.
const writeStarterCache = async (root, wsId, entry, getSdk) => {
  const merge = (row) =>
    trimStarterCache({ ...((row && row[STARTER_CACHE_KEY]) || {}), [wsId]: entry })
  const prev = root.userPrefs && typeof root.userPrefs === 'object' ? root.userPrefs : null
  if (prev) {
    root.update(
      { userPrefs: { ...prev, [STARTER_CACHE_KEY]: merge(prev) } },
      { preventFetch: true }
    )
  }
  if (!root.__navbarSaveQueue) root.__navbarSaveQueue = new Map()
  const queue = root.__navbarSaveQueue
  const prior = queue.get(wsId) || Promise.resolve()
  const task = prior
    .catch(() => {})
    .then(async () => {
      const sdk = getSdk()
      let live = root.userPrefs && typeof root.userPrefs === 'object' ? root.userPrefs : null
      if (!live) {
        const got = await sdk.execute('workspaceProject.userPreferences', 'get')
        live = got && typeof got === 'object' ? got : {}
      }
      // PER-PATH PATCH — names only `assistant_starters`, so no OTHER
      // preference is ever overwritten (CORE-USERPREFS-PATCH-PER-PATH-1).
      const row = await sdk.execute('workspaceProject.userPreferences', 'patch', {
        patch: { set: { [STARTER_CACHE_KEY]: merge(live) } }
      })
      const now = root.userPrefs && typeof root.userPrefs === 'object' ? root.userPrefs : null
      root.update(
        {
          userPrefs: now
            ? { ...now, [STARTER_CACHE_KEY]: merge(now) }
            : row && typeof row === 'object'
              ? row
              : { [STARTER_CACHE_KEY]: merge(live) }
        },
        { preventFetch: true }
      )
      return row
    })
  queue.set(wsId, task)
  try {
    await task
  } catch (e) {
    console.warn('[assistantStarters] cache persist failed', e && e.message)
  }
}

// Make sure THIS workspace has a fresh generated set; generate one in the
// background when it has none. Idempotent and cheap on every path that is
// not the one real generation:
//   • drawer closed (and not Fly's start screen asking) / workspace not
//     resolved / apps still loading → null
//   • the member's preferences row not read yet (`userPrefs` null) → null:
//     the set may be in it; the surfaces ask again when it lands
//   • fresh cache → the cached entry, no call
//   • a call already in flight for this workspace+lang → that promise
//   • a failure inside STARTER_FAIL_BACKOFF_MS → null (no retry storm). The
//     failure is remembered per workspace+lang, not per signature: what
//     fails is the service (no credits, managed AI down — both seen on dev
//     2026-09-10 as prose replies), and a new app wave does not change that.
//   • the signature moved during the settle window → null; the render
//     re-arms this with the settled signature, and only that one spends
//     the call.
// `opts.getSdk` / `opts.now` / `opts.settleMs` are test seams (the SDK is
// otherwise the shared singleton). `this` is the calling element when
// reached via el.call.
export const ensureWorkspaceStarters = async function (root, opts = {}) {
  const el = this
  const r = root || (el && el.state && el.state.root)
  if (!r || typeof r !== 'object' || typeof r.update !== 'function') return null
  // Two surfaces show the set: the drawer's empty chat (it asks when the
  // drawer is open) and Fly's start screen (functions/assistantFlyStart.js
  // asks with `surface: 'fly'` when it shows). One cached set serves both.
  const surfaceOpen = !!r.assistantOpen || (!!opts && opts.surface === 'fly')
  if (!surfaceOpen || !r.workspaceReady) return null
  const wsId = starterWorkspaceId(r)
  if (!wsId) return null
  // Installed apps still loading → their tiles and starters are not on root
  // yet; the Empty slot re-arms this when `workspaceApps` lands. Installed app
  // entries only (the ONE shared reader, installedAppRefs): the server-managed
  // records-grant row in settings.workspaceApps is not an app and never
  // publishes, so counting it kept the starters waiting for good.
  const declaredApps = installedAppRefs(r.workspace && r.workspace.settings)
  if (declaredApps.length && !Array.isArray(r.workspaceApps)) return null
  // STARTERS-ONCE (2026-11-01): the cached set lives in the member's
  // preferences row, and that row is read only AFTER the workspace is ready
  // (functions/loadPrivateData.js), while Fly's start screen asks as soon as
  // the workspace is ready. Judged on an unread row (`userPrefs` null — a cold
  // boot, or the window after a workspace switch wipes it), every home load
  // whose read outlasted the settle window spent a model call on a set the row
  // already held. Unread → nothing is decided; the start screen asks again
  // when the row lands (its stateDeps), the drawer's Empty slot at its render.
  if (!r.userPrefs || typeof r.userPrefs !== 'object') return null
  const now = typeof opts.now === 'function' ? opts.now : Date.now
  const getSdk = typeof opts.getSdk === 'function' ? opts.getSdk : shellGetSDK
  const settleMs = typeof opts.settleMs === 'number' ? opts.settleMs : STARTER_SETTLE_MS
  const lang = str(r.lang) || 'en'
  const hash = starterSignature(r, visibleStarterTiles(r))
  const cached = readStarterCache(r, wsId)
  if (starterCacheFresh(cached, { lang, hash, now: now() })) return cached
  const flightKey = wsId + ':' + lang
  if (!r.__aiStartersInflight) r.__aiStartersInflight = new Map()
  const inflight = r.__aiStartersInflight
  if (inflight.has(flightKey)) return inflight.get(flightKey)
  const failed = r.__aiStartersFailed || {}
  if (
    typeof failed[flightKey] === 'number' &&
    now() - failed[flightKey] < STARTER_FAIL_BACKOFF_MS
  ) {
    return null
  }
  const resolve = (key) => {
    try {
      return el && typeof el.call === 'function' ? el.call('polyglot', key) : key
    } catch (_) {
      return key
    }
  }
  const task = (async () => {
    r.update({ _aiStartersPending: true })
    try {
      // Let the app waves land, then read the signature the call is FOR.
      if (settleMs > 0) await new Promise((res) => setTimeout(res, settleMs))
      // a workspace switch wiped the row meanwhile: ask again when it lands
      if (!r.userPrefs || typeof r.userPrefs !== 'object') return null
      const tiles = namedStarterTiles(el, visibleStarterTiles(r))
      const settledHash = starterSignature(r, tiles)
      if (settledHash !== hash) return null
      if (starterCacheFresh(readStarterCache(r, wsId), { lang, hash, now: now() })) {
        return readStarterCache(r, wsId)
      }
      const snapshot = starterSnapshot(r, { resolve, tiles, now: now() })
      const sdk = getSdk()
      const ai = await sdk.getService('ai')
      const result = await ai.completion({
        messages: [{ role: 'user', content: starterUserMessage(snapshot) }],
        systemPromptOverride: starterSystemPrompt(lang),
        systemRef: STARTER_PURPOSE
      })
      const parsed = parseStarterReply(completionText(result))
      const items = starterItemsWithoutPeople(parsed, r.peopleAll)
      // Nothing left is the empty-set path (back off, keep the built set).
      // When the filter emptied it, the warning does not repeat the reply:
      // the reply names a person.
      if (!items.length && parsed.length)
        throw new Error('empty starter set: every item named a person')
      if (!items.length)
        throw new Error('empty starter set: ' + str(completionText(result)).slice(0, 120))
      const rule = starterRuleOf(lang)
      const entry = { v: STARTER_CACHE_VERSION, lang, hash, at: now(), items, ...(rule ? { rule } : {}) }
      await writeStarterCache(r, wsId, entry, getSdk)
      return entry
    } catch (e) {
      r.__aiStartersFailed = { ...(r.__aiStartersFailed || {}), [flightKey]: now() }
      console.warn('[assistantStarters] generation skipped:', e && e.message)
      return null
    } finally {
      inflight.delete(flightKey)
      r.update({ _aiStartersPending: false })
    }
  })()
  inflight.set(flightKey, task)
  return task
}
